(ns ui.components.combobox.grouped-picker
  "A picker that adds items from groups. It looks like the ZapKit Combobox: a
   trigger that opens a list with a search field. The list has groups that a user
   can open and close, and each item has a checkbox. A user who checks an item
   adds it, and the owner removes the item from the groups. The mockup of the
   Dealflow pages has this picker in the Edit columns modal. ZapKit has no such
   component, so the groups and the items are ours. The classes of the trigger,
   the list, the search field and the empty text come from the generated
   `ui.components.combobox.zapkit-styles`."
  (:require
   [clojure.string :as string]
   [common.util :as cu]
   [reagent.core :as r]
   [ui.components.combobox.zapkit-styles :as styles]
   [ui.components.icon-button.zapkit-styles :as icon-button-styles]
   [ui.components.input.checkbox :as checkbox]
   [ui.components.zapkit-variants :as variants]
   [ui.dom-globals :as dg]
   [ui.icons :as icons]
   [ui.util :as uu]))

(def dom-overrides
  "Classes of the mockup that ZapKit does not have. The groups and their items
   are not in the ZapKit Combobox. Each class string is copied from the DOM of the
   mockup.

   `:group-row` and `:group-label` extend `[:item :base]`: the row of a group has
   the button that opens it, and the name of the group.
   `:item-row` extends `[:item :base]` for an item with a checkbox. It is
   indented under its group.
   `:message` extends `[:empty :base]`: the text has the padding of an item."
  {:group-row   "tw2-flex tw2-items-center tw2-gap-1 tw2-px-dropdown-px tw2-py-dropdown-py"
   :group-label "tw2-min-w-0 tw2-flex-1 tw2-text-left tw2-text-label-md-strong tw2-text-primary"
   :item-row    "tw2-px-dropdown-px tw2-py-dropdown-py tw2-pl-9"
   :message     "tw2-px-dropdown-px tw2-py-dropdown-py"})

(defn filter-groups
  "Return the groups that have an item with `query` in its label, and only those
   items. The search ignores the case and the spaces around the query. A blank
   query keeps all the items. A group with no item is not in the result.
   A group is a map with `:key`, `:label` and `:items`, and an item is a map with
   `:value` and `:label`."
  [{:keys [groups query]}]
  (let [needle (cu/str-trim-lcase query)]
    (->> groups
         (map (fn [group]
                (update group :items
                        (fn [items]
                          (vec (filter (fn [item]
                                         (or (string/blank? needle)
                                             (string/includes? (cu/str-trim-lcase (:label item))
                                                               needle)))
                                       items))))))
         (filter (comp seq :items))
         vec)))

(defn group-collapsed?
  "True when a group is closed. A group is closed until a user opens it. While a
   query is typed, all the groups are open, so the user sees the matches."
  [{:keys [query expanded group-key]}]
  (and (string/blank? query)
       (not (contains? expanded group-key))))

(defn- icon-button-class
  []
  (let [root (:root icon-button-styles/icon-button)]
    (uu/cm (variants/slot-class root {:variant :default
                                      :compact false}))))

(defn group-view
  [{:keys [group query expanded labels on-toggle-group on-select]}]
  (let [collapsed? (group-collapsed? {:query     query
                                      :expanded  expanded
                                      :group-key (:key group)})
        toggle-label (str (if collapsed?
                            (:expand-group labels)
                            (:collapse-group labels))
                          " "
                          (:label group))]
    [:div
     {:class "tw2-flex tw2-flex-col"}
     [:div
      {:class (:group-row dom-overrides)}
      [:button
       {:type          "button"
        :aria-label    toggle-label
        :aria-expanded (not collapsed?)
        :class         (icon-button-class)
        :on-click      (fn [_]
                         (on-toggle-group (:key group)))}
       (if collapsed?
         [icons/chevron-right-icon
          {:class       (get-in icon-button-styles/icon-button [:icon :size :md])
           :aria-hidden true}]
         [icons/chevron-down-icon
          {:class       (get-in icon-button-styles/icon-button [:icon :size :md])
           :aria-hidden true}])]
      [:button
       {:type          "button"
        :aria-expanded (not collapsed?)
        :class         (:group-label dom-overrides)
        :on-click      (fn [_]
                         (on-toggle-group (:key group)))}
       (:label group)]]
     (when-not collapsed?
       [:div
        {:class "tw2-flex tw2-flex-col"}
        (for [item (:items group)]
          ^{:key (pr-str (:value item))}
          [:div
           {:class (:item-row dom-overrides)}
           [:label
            {:class "tw2-flex tw2-min-w-0 tw2-cursor-pointer tw2-items-center tw2-gap-checkbox-gap"}
            [checkbox/view
             {:value         false
              :style-variant :tw2
              :on-change     (fn [checked?]
                               (when checked?
                                 (on-select (:value item))))}]
            [:span
             {:class "tw2-min-w-0 tw2-truncate tw2-text-body-md tw2-text-secondary"}
             (:label item)]]])])]))

(defn- chevron-view
  [open?]
  [:span
   {:class (get-in styles/combobox [:chevron :base])}
   [icons/chevron-down-icon
    {:class       (variants/slot-class (:chevron-down styles/combobox) {:open open?})
     :aria-hidden true}]
   [icons/chevron-up-icon
    {:class       (variants/slot-class (:chevron-up styles/combobox) {:open open?})
     :aria-hidden true}]])

(defn picker
  "The markup of the picker. `view` keeps the state and gives it to this
   function. See `view` for the props. `open?`, `query` and `expanded` are the
   state: the list is open, the text in the search field, and the set of the
   groups that the user has opened."
  [{:keys [open? query expanded groups labels trigger-label root-ref
           on-toggle on-query-change on-toggle-group on-select]}]
  (let [shown-groups (filter-groups {:groups groups
                                     :query  query})]
    [:div
     {:ref   root-ref
      :class (get-in styles/combobox [:root :base])}
     [:button
      {:type          "button"
       :aria-expanded (boolean open?)
       :aria-haspopup "listbox"
       :class         (variants/slot-class (:trigger styles/combobox)
                                           {:state    (if open?
                                                        :focus
                                                        :default)
                                            :selected false})
       :on-click      (fn [_]
                        (on-toggle))}
      [:span
       {:class (get-in styles/combobox [:trigger-label :base])}
       trigger-label]
      [chevron-view (boolean open?)]]
     (when open?
       [:div
        {:role  "listbox"
         :class (variants/slot-class (:list styles/combobox) {:visible true})}
        [:div
         {:class (get-in styles/combobox [:search :base])}
         [:div
          {:class (get-in styles/combobox [:search-field-wrap :base])}
          [:span
           {:class (get-in styles/combobox [:search-icon-wrap :base])}
           [icons/search-icon
            {:class       (get-in styles/combobox [:search-icon :base])
             :aria-hidden true}]]
          [:input
           {:type        "text"
            :value       (or query "")
            :placeholder (:search-placeholder labels)
            :class       (variants/slot-class (:search-field styles/combobox) {:state :default})
            :on-change   (fn [event]
                           (on-query-change (dg/event-target-value event)))}]]]
        [:div
         {:class (get-in styles/combobox [:options :base])}
         (if (seq shown-groups)
           (for [group shown-groups]
             ^{:key (pr-str (:key group))}
             [group-view
              {:group           group
               :query           query
               :expanded        expanded
               :labels          labels
               :on-toggle-group on-toggle-group
               :on-select       on-select}])
           [:p
            {:class (uu/cm (get-in styles/combobox [:empty :base])
                           (:message dom-overrides))}
            (if (string/blank? query)
              (:empty labels)
              (:empty-search labels))])]])]))

(defn view
  "Render the picker.

   `:trigger-label` is the text of the trigger.
   `:groups` is a vector of maps with `:key`, `:label` and `:items`. An item is a
   map with `:value` and `:label`.
   `:on-select` is called with the `:value` of an item that the user checks.
   `:labels` has the texts: `:search-placeholder`, `:empty` (no item is left),
   `:empty-search` (no item has the query), `:expand-group` and `:collapse-group`
   (the start of the name of the button of a group)."
  [{:keys [trigger-label groups labels on-select]}]
  (r/with-let [open?* (r/atom false)
               query* (r/atom "")
               expanded* (r/atom #{})
               root* (atom nil)
               close! (fn []
                        (reset! open?* false))
               outside-handler (fn [event]
                                 (when (and @open?*
                                            (not (dg/node-contains? @root* (dg/target event))))
                                   (close!)))
               _ (dg/add-event-listener! js/document "pointerdown" outside-handler)]
    [picker
     {:open?           @open?*
      :query           @query*
      :expanded        @expanded*
      :groups          groups
      :labels          labels
      :trigger-label   trigger-label
      :root-ref        (fn [node]
                         (reset! root* node))
      :on-toggle       (fn []
                         (let [open? (swap! open?* not)]
                           (when open?
                             (reset! query* "")
                             (reset! expanded* #{}))))
      :on-query-change (fn [text]
                         (reset! query* text))
      :on-toggle-group (fn [group-key]
                         (swap! expanded* (fn [expanded]
                                            (if (contains? expanded group-key)
                                              (disj expanded group-key)
                                              (conj expanded group-key)))))
      :on-select       on-select}]
    (finally
      (dg/remove-event-listener! js/document "pointerdown" outside-handler))))
